/* =============================================================================
 * motion.js — takumi design system / モーションランタイム
 * -----------------------------------------------------------------------------
 * 依存ゼロ。バニラJS（ES2019相当）。Shopifyテーマの assets/ にそのまま置ける。
 * motion.css とセットで使う。
 *
 * 【設置方法（Shopify: layout/theme.liquid）】
 *   <head> の先頭付近に、下記を「同期スクリプトで」置く（FOUC＝初期状態のチラつき防止）
 *     <script>
 *       document.documentElement.classList.add('mo-js');
 *       // セーフティネット: motion.js が届かなかったら初期非表示を解除して素の見た目に戻す
 *       setTimeout(function () {
 *         if (!window.Motion) document.documentElement.classList.remove('mo-js');
 *       }, 2500);
 *     </script>
 *   CSSを読み込む
 *     {{ 'motion.css' | asset_url | stylesheet_tag }}
 *   </body> 直前、または head 内に defer で
 *     <script src="{{ 'motion.js' | asset_url }}" defer></script>
 *
 * 【Progressive Enhancement の担保】
 *   - JS を切っている環境: html.mo-js が付かない → 初期非表示ルールが一切効かない
 *   - JS は動くが motion.js の取得に失敗: 上のセーフティタイマーが mo-js を外す
 *   - どちらの場合も「動かないが、全部読める」状態に着地する
 *
 * 【機能一覧】
 *   data-mo="up|fade|scale|blur|mask-up|clip-up|..."  スクロール出現
 *   data-mo-cascade="up"                              子要素の連鎖出現
 *   data-mo-split="chars|words|lines"                 文字分割（キネティックテキスト）
 *   data-mo-parallax="0.15"                           視差
 *   data-mo-scrub="cover|pin|enter"                   スクロール進捗を --mo-progress に
 *   data-mo-hscroll                                   縦スクロール→横移動
 *   data-mo-counter                                   数字カウントアップ
 *   data-mo-marquee                                   無限マーキー
 *   data-mo-stack                                     stickyカード積み上げ
 *   data-mo-magnetic="0.3"                            マグネティックボタン
 *   Motion.flyToCart(img, cartIcon)                   カートに飛ぶ演出
 *
 * 【性能規約】
 *   - 触るプロパティは transform / opacity / filter / CSS変数 のみ
 *   - スクロール中のレイアウト読み取り（getBoundingClientRect）は
 *     1フレームにつき「読み込み → 書き込み」の順に一括（layout thrashing 回避）
 *   - rAFループはスクロールが止まったら自動停止（アイドル時 0% CPU）
 * ========================================================================== */

(function () {
  'use strict';

  /* ===========================================================================
   * 0. 基盤ユーティリティ
   * ======================================================================== */

  var VERSION = '1.0.0';
  var root = document.documentElement;

  /** OS の「視差効果を減らす」 */
  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** 実際にマウスが使える端末か（タッチでは magnetic 等を無効化） */
  var fineQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  function prefersReduced() {
    // テーマ設定の強制OFFも同じ扱いにする
    return reduceQuery.matches || root.getAttribute('data-motion') === 'off';
  }

  function num(value, fallback) {
    var n = parseFloat(value);
    return isNaN(n) ? fallback : n;
  }

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  /** 算出済みスタイルから px 値を読む（top: 5vh のような相対単位を実px に解決する） */
  function pxOfComputed(el, prop) {
    var v = parseFloat(window.getComputedStyle(el)[prop]);
    return isNaN(v) ? 0 : v;
  }

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /** scope 自身もマッチ対象に含めた検索（Shopify section 再描画で必要） */
  function qsaSelf(selector, scope) {
    var list = qsa(selector, scope);
    if (scope && scope.nodeType === 1 && scope.matches && scope.matches(selector)) {
      list.unshift(scope);
    }
    return list;
  }

  /* ---------------------------------------------------------------------------
   * 共有 rAF スケジューラ
   *   スクロール/リサイズで起動し、変化が収まったら自動的に止まる。
   *   すべてのスクロール連動モジュールがこの 1 ループに相乗りする。
   * ------------------------------------------------------------------------ */
  var Ticker = (function () {
    var readers = []; // 各フレームで「読む」処理（rect取得）
    var writers = []; // 各フレームで「書く」処理（style更新）
    var running = false;
    var idleFrames = 0;
    var lastScrollY = -1;

    function frame() {
      var y = window.scrollY;
      var moved = y !== lastScrollY;
      lastScrollY = y;

      // 1) 読み込みフェーズ（DOM への書き込みは一切しない）
      for (var i = 0; i < readers.length; i++) readers[i]();
      // 2) 書き込みフェーズ
      for (var j = 0; j < writers.length; j++) writers[j]();

      // 動きが止まって 3 フレーム経ったらループを畳む（バッテリー保護）
      idleFrames = moved ? 0 : idleFrames + 1;
      if (idleFrames > 3 || (readers.length === 0 && writers.length === 0)) {
        running = false;
        return;
      }
      requestAnimationFrame(frame);
    }

    function kick() {
      idleFrames = 0;
      if (running) return;
      running = true;
      requestAnimationFrame(frame);
    }

    return {
      addReader: function (fn) {
        readers.push(fn);
        kick();
      },
      addWriter: function (fn) {
        writers.push(fn);
        kick();
      },
      kick: kick,
    };
  })();

  window.addEventListener('scroll', Ticker.kick, { passive: true });
  window.addEventListener('resize', Ticker.kick, { passive: true });
  window.addEventListener('orientationchange', Ticker.kick, { passive: true });

  /* ---------------------------------------------------------------------------
   * 「通り過ぎた要素」の救済（重要）
   *
   * IntersectionObserver は「今の交差状態」しか通知しない。
   * アンカーリンク(#section)でのジャンプ、リロード時のスクロール位置復元、
   * 「戻る」でのページ中腹への復帰では、**一度も交差しないまま画面上方へ抜けた要素**が
   * 発生し、opacity:0 のまま永久に残る（＝コンテンツが消える重大バグ）。
   *
   * 対策:
   *   a) 初期化時に「画面内 or すでに通り過ぎた」要素は即時表示する
   *   b) 1画面分を超えるスクロールジャンプを検知したら、未処理要素を掃く
   * ------------------------------------------------------------------------ */
  var pendingInView = new Map(); // el -> 表示コールバック

  function registerPending(el, cb) {
    pendingInView.set(el, cb);
  }

  function resolvePending(el) {
    pendingInView.delete(el);
  }

  /** 画面上方へ抜けてしまった未処理要素を救済する */
  function sweepPassed() {
    if (!pendingInView.size) return;
    var due = [];
    pendingInView.forEach(function (cb, el) {
      // bottom < 0 = 完全に画面上方へ抜けた = IO はもう二度と発火しない
      if (el.getBoundingClientRect().bottom < 0) due.push([el, cb]);
    });
    for (var i = 0; i < due.length; i++) {
      pendingInView.delete(due[i][0]);
      due[i][1]();
    }
  }

  var lastSweepY = window.scrollY;
  window.addEventListener(
    'scroll',
    function () {
      var y = window.scrollY;
      // 1画面分を超える移動 = ジャンプ。通常のスクロールでは発火しない
      if (Math.abs(y - lastSweepY) > window.innerHeight) sweepPassed();
      lastSweepY = y;
    },
    { passive: true }
  );
  window.addEventListener('hashchange', function () {
    setTimeout(sweepPassed, 60);
  });
  window.addEventListener('load', function () {
    setTimeout(sweepPassed, 60);
  });
  window.addEventListener('pageshow', function () {
    setTimeout(sweepPassed, 60);
  });

  /* ===========================================================================
   * 1. reveal — スクロール出現
   * ======================================================================== */

  var revealObserver = null;

  function ensureRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          var el = entry.target;
          if (entry.isIntersecting) {
            showReveal(el);
            if (el.getAttribute('data-mo-once') !== 'false') {
              revealObserver.unobserve(el);
              resolvePending(el);
            }
          } else if (el.getAttribute('data-mo-once') === 'false') {
            hideReveal(el);
          }
        }
      },
      {
        // 下端から 10% 内側で発火 = 「画面に入りきる直前」に動き始める
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.01,
      }
    );
    return revealObserver;
  }

  /* CSS 側の --mo-stagger-max（既定8）と必ず一致させること。
   * CSS は min(--mo-i, --mo-stagger-max) で遅延を打ち切るので、JS が
   * 生の index で待つと 100 件のリストで「6秒間 will-change が乗り続ける」ことになる。 */
  var STAGGER_MAX = 8;

  function showReveal(el) {
    el.classList.add('is-mo-in');

    // will-change を降ろすのは「実際にトランジションが終わったとき」が正。
    // タイマーはあくまで保険（transition が発火しないケース＝値が変化しない要素用）。
    var done = function () {
      el.removeEventListener('transitionend', done);
      window.clearTimeout(el.__moDoneTimer);
      el.__moDoneTimer = 0;
      el.classList.add('is-mo-done');
    };
    el.addEventListener('transitionend', done);

    var dur = num(el.getAttribute('data-mo-dur'), 480);
    var delay = num(el.getAttribute('data-mo-delay'), 0);
    var idx = Math.min(num(el.style.getPropertyValue('--mo-i'), 0), STAGGER_MAX);
    // stagger 間隔は cascade の種別で 40/60/90ms と変わる。保険なので最大値(90)で見積もる。
    el.__moDoneTimer = window.setTimeout(done, dur + delay + idx * 90 + 200);
  }

  function hideReveal(el) {
    el.classList.remove('is-mo-in', 'is-mo-done');
  }

  /**
   * 「すでに見えている、または通り過ぎている」か。
   * true なら IO を待たずに即時表示する。
   *   - 画面内 ＝ ファーストビュー。IO 待ちにすると LCP が悪化する
   *   - 画面上方 ＝ リロード時のスクロール位置復元。IO は永久に発火しない
   */
  function isInOrPastViewport(el) {
    return el.getBoundingClientRect().top < window.innerHeight;
  }

  function initReveal(scope) {
    // 1) cascade: 親から子へ data-mo と index を配る
    qsaSelf('[data-mo-cascade]', scope).forEach(function (group) {
      if (group.hasAttribute('data-mo-cascade-ready')) return;
      group.setAttribute('data-mo-cascade-ready', '');
      var type = group.getAttribute('data-mo-cascade') || 'up';
      if (type === 'tight' || type === 'loose') type = 'up';
      var targets = group.hasAttribute('data-mo-cascade-target')
        ? qsa(group.getAttribute('data-mo-cascade-target'), group)
        : Array.prototype.slice.call(group.children);
      targets.forEach(function (child, i) {
        if (!child.hasAttribute('data-mo')) child.setAttribute('data-mo', type);
        child.style.setProperty('--mo-i', String(i));
      });
    });

    // 2) 個別 reveal
    var io = ensureRevealObserver();
    qsaSelf('[data-mo]', scope).forEach(function (el) {
      if (el.hasAttribute('data-mo-ready')) return;
      el.setAttribute('data-mo-ready', '');

      var delay = el.getAttribute('data-mo-delay');
      if (delay) el.style.setProperty('--mo-delay', num(delay, 0) + 'ms');
      var dur = el.getAttribute('data-mo-dur');
      if (dur) el.style.setProperty('--mo-dur', num(dur, 480) + 'ms');
      var ease = el.getAttribute('data-mo-ease');
      if (ease) el.style.setProperty('--mo-ease', 'var(--mo-ease-' + ease + ')');

      if (prefersReduced()) {
        // 動かさないが「見える」ことは保証する
        el.classList.add('is-mo-in', 'is-mo-done');
        return;
      }
      if (isInOrPastViewport(el)) {
        // ファーストビューは即表示。ヒーロー画像を IO 待ちにすると LCP が悪化する
        showReveal(el);
        return;
      }
      io.observe(el);
      registerPending(el, function () {
        showReveal(el);
        io.unobserve(el);
      });
    });
  }

  /* ===========================================================================
   * 2. split — 文字/単語/行への分割（キネティックテキスト）
   *    アクセシビリティ: 親に aria-label を付け、分割後のスパンは aria-hidden。
   *    これをやらないとスクリーンリーダーが「か / き / く」と1文字ずつ読む。
   * ======================================================================== */

  function splitText(el, mode) {
    if (el.hasAttribute('data-mo-split-ready')) return;
    var text = el.textContent.replace(/\s+/g, ' ').trim();
    // ⚠️ 空要素でも ready を立てる。立てないと「毎回 splitText を試みる要素」が
    //    残り、MutationObserver 経由の再 init のたびに無駄な走査が走る。
    el.setAttribute('data-mo-split-ready', '');
    if (!text) return;

    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', text);
    el.classList.add('mo-split');

    var index = 0;
    function makeSpan(cls, content) {
      var span = document.createElement('span');
      span.className = cls;
      span.setAttribute('aria-hidden', 'true');
      span.textContent = content;
      span.style.setProperty('--mo-i', String(index++));
      return span;
    }

    if (mode === 'lines') {
      // 行分割は「実際に折り返された位置」を測るしかない（CSSの折返しはJSで予測できない）。
      // 手順: いったん単語/文字スパンに割る → offsetTop でグループ化 → 行スパンに包み直す。
      buildLines(el, text, makeSpan);
    } else {
      var frag = document.createDocumentFragment();
      if (mode === 'words') {
        text.split(' ').forEach(function (word, i, arr) {
          frag.appendChild(makeSpan('mo-word', i === arr.length - 1 ? word : word + ' '));
        });
      } else {
        // chars（既定）。日本語は単語境界が無いので基本こちら。
        // Array.from でサロゲートペア（絵文字等）を壊さずに分割する。
        Array.from(text).forEach(function (ch) {
          frag.appendChild(makeSpan('mo-char', ch));
        });
      }
      el.textContent = '';
      el.appendChild(frag);
    }

    el.setAttribute('data-mo-split-anim', '');
  }

  /**
   * data-mo-split="lines" の実装。
   * 日本語は単語境界が無いため、まず「文字」単位で置いて offsetTop を測り、
   * 同じ行に乗った文字をまとめて 1 本の .mo-line に詰め直す。
   * 各行は .mo-mask（overflow:hidden）に包むので、そのままマスク上げに使える。
   */
  function buildLines(el, text, makeSpan) {
    var probe = document.createDocumentFragment();
    var units = [];
    // 欧文は単語で、和文は文字で測る（欧文を文字で測ると単語が分断される）
    var isLatinish = /^[\x20-\x7F -ɏ]+$/.test(text);
    var pieces = isLatinish ? text.split(' ') : Array.from(text);
    pieces.forEach(function (piece, i) {
      var s = document.createElement('span');
      s.textContent = isLatinish && i !== pieces.length - 1 ? piece + ' ' : piece;
      s.style.display = 'inline-block'; // offsetTop を安定させる
      probe.appendChild(s);
      units.push(s);
    });
    el.textContent = '';
    el.appendChild(probe);

    // ---- 読み取りフェーズ（ここで DOM を書き換えない）----
    var tops = units.map(function (s) {
      return s.offsetTop;
    });

    // ---- 行にグループ化 ----
    var lines = [];
    var cur = null;
    for (var i = 0; i < units.length; i++) {
      if (!cur || Math.abs(tops[i] - cur.top) > 2) {
        cur = { top: tops[i], text: '' };
        lines.push(cur);
      }
      cur.text += units[i].textContent;
    }

    // ---- 書き込みフェーズ ----
    var frag = document.createDocumentFragment();
    lines.forEach(function (line) {
      var mask = document.createElement('span');
      mask.className = 'mo-mask';
      mask.setAttribute('aria-hidden', 'true');
      mask.appendChild(makeSpan('mo-line', line.text));
      frag.appendChild(mask);
    });
    el.textContent = '';
    el.appendChild(frag);
  }

  var splitObserver = null;

  function ensureSplitObserver() {
    if (splitObserver) return splitObserver;
    splitObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-mo-in');
          splitObserver.unobserve(entry.target);
          resolvePending(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.05 }
    );
    return splitObserver;
  }

  function initSplit(scope) {
    // ⚠️ 未処理のものだけに絞る。絞らないと init() が呼ばれるたびに
    //    IntersectionObserver が新規生成され、表示済み要素まで再登録される
    //    （MutationObserver が init を何度も呼ぶため、実測でリークする）。
    var targets = qsaSelf('[data-mo-split]', scope).filter(function (el) {
      return !el.hasAttribute('data-mo-split-init');
    });
    if (!targets.length) return;

    var io = ensureSplitObserver();
    targets.forEach(function (el) {
      el.setAttribute('data-mo-split-init', '');
      splitText(el, el.getAttribute('data-mo-split') || 'chars');
      if (prefersReduced() || isInOrPastViewport(el)) {
        el.classList.add('is-mo-in');
        return;
      }
      io.observe(el);
      registerPending(el, function () {
        el.classList.add('is-mo-in');
        io.unobserve(el);
      });
    });
  }

  /* ===========================================================================
   * 3. parallax — 視差
   *    data-mo-parallax="0.15"           速度（0.05〜0.25が実用域。0.3超は酔う）
   *    data-mo-parallax-axis="y|x"       既定 y
   *    data-mo-parallax-max="120"        最大移動量(px)。安全弁
   * ======================================================================== */

  var parallaxItems = [];
  var parallaxBound = false;

  function initParallax(scope) {
    if (prefersReduced()) return;
    var found = qsaSelf('[data-mo-parallax]', scope).filter(function (el) {
      return !el.hasAttribute('data-mo-parallax-ready');
    });
    if (!found.length) return;

    var gate = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var item = entry.target.__moParallax;
          if (item) item.active = entry.isIntersecting;
        });
        Ticker.kick();
      },
      { rootMargin: '20% 0px 20% 0px' }
    );

    found.forEach(function (el) {
      el.setAttribute('data-mo-parallax-ready', '');
      var item = {
        el: el,
        speed: clamp(num(el.getAttribute('data-mo-parallax'), 0.15), -0.5, 0.5),
        axis: el.getAttribute('data-mo-parallax-axis') === 'x' ? 'x' : 'y',
        max: num(el.getAttribute('data-mo-parallax-max'), 160),
        active: false,
        offset: 0,
      };
      el.__moParallax = item;
      parallaxItems.push(item);
      gate.observe(el);
    });

    if (parallaxBound) return;
    parallaxBound = true;

    Ticker.addReader(function () {
      var vh = window.innerHeight;
      // Shopify のセクション再描画で外れた要素が溜まり続けるのを防ぐ
      // （放置すると毎フレーム「もう画面に無い要素」の rect を測り続ける）
      for (var p = parallaxItems.length - 1; p >= 0; p--) {
        if (!parallaxItems[p].el.isConnected) parallaxItems.splice(p, 1);
      }
      for (var i = 0; i < parallaxItems.length; i++) {
        var it = parallaxItems[i];
        if (!it.active) continue;
        var rect = it.el.getBoundingClientRect();
        // 要素の中心が画面中心からどれだけ離れているか（-vh/2 〜 +vh/2 付近）
        var delta = rect.top + rect.height / 2 - vh / 2;
        it.offset = clamp(-delta * it.speed, -it.max, it.max);
      }
    });

    Ticker.addWriter(function () {
      for (var i = 0; i < parallaxItems.length; i++) {
        var it = parallaxItems[i];
        if (!it.active) continue;
        it.el.style.setProperty(
          it.axis === 'x' ? '--mo-par-x' : '--mo-par-y',
          it.offset.toFixed(2) + 'px'
        );
      }
    });
  }

  /* ===========================================================================
   * 4. scrub — スクロール進捗を --mo-progress (0→1) に書き込む
   *    data-mo-scrub="cover"  要素が画面を横切る間で 0→1（既定）
   *    data-mo-scrub="pin"    .mo-pin の内側で固定表示中 0→1（pin & scrub 用）
   *    data-mo-scrub="enter"  下端から入り、画面中央に来るまでで 0→1
   *    data-mo-scrub-target="#id"  進捗の書き込み先（既定は自分自身）
   * ======================================================================== */

  var scrubItems = [];
  var scrubBound = false;

  function initScrub(scope) {
    var found = qsaSelf('[data-mo-scrub]', scope).filter(function (el) {
      return !el.hasAttribute('data-mo-scrub-ready');
    });

    // 横スクロールセクションは scrub の特殊形として同じ仕組みに乗せる
    qsaSelf('[data-mo-hscroll]', scope).forEach(function (el) {
      if (el.hasAttribute('data-mo-scrub-ready')) return;
      el.setAttribute('data-mo-scrub', 'pin');
      if (found.indexOf(el) === -1) found.push(el);
    });

    if (!found.length) return;

    var gate = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var item = entry.target.__moScrub;
          if (item) item.active = entry.isIntersecting;
        });
        Ticker.kick();
      },
      { rootMargin: '10% 0px 10% 0px' }
    );

    found.forEach(function (el) {
      el.setAttribute('data-mo-scrub-ready', '');
      var targetSel = el.getAttribute('data-mo-scrub-target');
      var item = {
        el: el,
        target: targetSel ? document.querySelector(targetSel) || el : el,
        mode: el.getAttribute('data-mo-scrub') || 'cover',
        progress: 0,
        active: false,
        isHscroll: el.hasAttribute('data-mo-hscroll'),
        track: el.querySelector('.mo-hscroll__track'),
      };
      el.__moScrub = item;
      scrubItems.push(item);
      gate.observe(el);
      layoutHscroll(item);
    });

    if (!scrubBound) {
      scrubBound = true;
      Ticker.addReader(function () {
        var vh = window.innerHeight;
        for (var s = scrubItems.length - 1; s >= 0; s--) {
          if (!scrubItems[s].el.isConnected) scrubItems.splice(s, 1);
        }
        for (var i = 0; i < scrubItems.length; i++) {
          var it = scrubItems[i];
          if (!it.active) continue;
          var r = it.el.getBoundingClientRect();
          var p;
          if (it.mode === 'pin') {
            // 要素の上端が画面上端に来た瞬間 0、下端が画面下端に来た瞬間 1
            var span = r.height - vh;
            p = span > 0 ? -r.top / span : 0;
          } else if (it.mode === 'enter') {
            p = (vh - r.top) / (vh * 0.5 + r.height * 0.5);
          } else {
            // cover: 下端から入って上端から抜けるまで
            p = (vh - r.top) / (vh + r.height);
          }
          it.progress = clamp(p, 0, 1);
        }
      });
      Ticker.addWriter(function () {
        for (var i = 0; i < scrubItems.length; i++) {
          var it = scrubItems[i];
          if (!it.active) continue;
          it.target.style.setProperty('--mo-progress', it.progress.toFixed(4));
        }
      });
      window.addEventListener(
        'resize',
        function () {
          scrubItems.forEach(layoutHscroll);
        },
        { passive: true }
      );
    }
  }

  /**
   * 横スクロールの移動量を実測して CSS 変数に入れる。
   * 併せて、必要なスクロール距離ぶんの高さをラッパーに与える
   * （高さが足りないと「横に流れ切る前にセクションが終わる」）。
   */
  function layoutHscroll(item) {
    if (!item.isHscroll || !item.track) return;

    // ⚠️ window.innerWidth を使ってはいけない。
    //    (a) innerWidth はクラシックスクロールバー(Win/一部Linux)の幅を含むため、
    //        レイアウト幅より 15px ほど大きく、最後のカードがその分だけ見切れる。
    //    (b) セクションが全幅とは限らない（左右に余白のあるコンテナに入っている場合、
    //        基準にすべきなのは画面幅ではなく「実際に見えている枠」の幅）。
    //    見えている枠 = sticky な stage。無ければセクション自身の内容幅を使う。
    var stage = item.stage || item.el.querySelector('.mo-pin__stage') || item.el;
    item.stage = stage;
    var viewW = stage.clientWidth || item.el.clientWidth || document.documentElement.clientWidth;
    var viewH = stage.clientHeight || window.innerHeight;

    var shift = item.track.scrollWidth - viewW;
    if (shift < 0) shift = 0;
    item.el.style.setProperty('--mo-hscroll-shift', '-' + shift + 'px');
    // 移動量 + 1画面 = セクションの高さ（1px横に動かすのに1pxスクロールする素直な比率）
    var ratio = num(item.el.getAttribute('data-mo-hscroll-ratio'), 1);
    item.el.style.minHeight = viewH + shift * ratio + 'px';
  }

  /* ===========================================================================
   * 5. counter — 数字カウントアップ
   *    <span data-mo-counter data-mo-to="1280" data-mo-dur="1400"
   *          data-mo-decimals="0" data-mo-prefix="¥" data-mo-suffix="+">1,280</span>
   *    ※ HTML には最終値を書いておくこと（JS無効時にそのまま正しい）
   * ======================================================================== */

  function initCounter(scope) {
    var targets = qsaSelf('[data-mo-counter]', scope).filter(function (el) {
      return !el.hasAttribute('data-mo-counter-ready');
    });
    if (!targets.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          runCounter(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    targets.forEach(function (el) {
      el.setAttribute('data-mo-counter-ready', '');
      var to = num(el.getAttribute('data-mo-to'), num(el.textContent.replace(/[^\d.-]/g, ''), 0));
      el.__moTo = to;
      if (prefersReduced()) return; // 最終値のまま置く
      io.observe(el);
    });
  }

  function runCounter(el) {
    var to = el.__moTo;
    var from = num(el.getAttribute('data-mo-from'), 0);
    var dur = num(el.getAttribute('data-mo-dur'), 1400);
    var decimals = num(el.getAttribute('data-mo-decimals'), 0);
    var prefix = el.getAttribute('data-mo-prefix') || '';
    var suffix = el.getAttribute('data-mo-suffix') || '';
    var locale = el.getAttribute('data-mo-locale') || 'ja-JP';
    var fmt = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    // null 起点にする（0 を「未設定」と見なすと now===0 のフレームで 1 フレーム分ズレる）
    var start = null;

    // easeOutExpo: 最初に一気に進み、最後にゆっくり着地する = 数字が「決まる」感じが出る
    function ease(t) {
      return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
    }

    function step(now) {
      if (start === null) start = now;
      var t = clamp((now - start) / dur, 0, 1);
      var value = from + (to - from) * ease(t);
      el.textContent = prefix + fmt.format(value) + suffix;
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  /* ===========================================================================
   * 6. marquee — 無限に流れる帯
   *    <div class="mo-marquee" data-mo-marquee data-mo-speed="60"
   *         data-mo-direction="left" data-pause-on-hover>
   *      <div class="mo-marquee__track"> …中身… </div>
   *    </div>
   *    speed は px/秒。60 なら 1秒に60px 流れる（幅が変わっても体感速度が一定）
   * ======================================================================== */

  function initMarquee(scope) {
    qsaSelf('[data-mo-marquee]', scope).forEach(function (el) {
      if (el.hasAttribute('data-mo-marquee-ready')) return;
      el.setAttribute('data-mo-marquee-ready', '');

      var track = el.querySelector('.mo-marquee__track');
      if (!track) {
        // 素の子要素しか無い場合はトラックを作って包む
        track = document.createElement('div');
        track.className = 'mo-marquee__track';
        while (el.firstChild) track.appendChild(el.firstChild);
        el.appendChild(track);
      }

      // speed <= 0 は duration が Infinity / 負値になり CSS ごと無効化されるので弾く
      var speed = num(el.getAttribute('data-mo-speed'), 60);
      if (!(speed > 0)) speed = 60;
      if (el.getAttribute('data-mo-direction') === 'right') {
        el.style.setProperty('--mo-marquee-dir', 'reverse');
      }

      function layout() {
        // 既存の複製を消してから測る
        qsa('[data-mo-marquee-clone]', el).forEach(function (c) {
          c.remove();
        });
        var width = track.scrollWidth;
        if (!width) return;

        // 画面幅の2倍を埋めるまで複製（隙間が出ないための最低条件）
        var need = Math.max(2, Math.ceil((el.offsetWidth * 2) / width));
        for (var i = 1; i < need; i++) {
          var clone = track.cloneNode(true);
          clone.setAttribute('data-mo-marquee-clone', '');
          clone.setAttribute('aria-hidden', 'true'); // 読み上げの重複を防ぐ
          el.appendChild(clone);
        }

        // ⚠️ 1周で実際に動く距離は「トラック幅 + gap」。
        //    CSS 側のキーフレームが translate3d(calc(-100% - var(--mo-marquee-gap))) だからで、
        //    gap を無視して width/speed にすると指定より速く流れる（gap 2rem・幅400px なら 8%ズレ）。
        var gapPx = parseFloat(getComputedStyle(el).columnGap);
        if (isNaN(gapPx)) gapPx = 0;
        el.style.setProperty('--mo-marquee-dur', (width + gapPx) / speed + 's');
      }

      layout();
      // 画像の遅延ロードで幅が変わるため、フォント/画像確定後に測り直す
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
      window.addEventListener('resize', debounce(layout, 200), { passive: true });
    });
  }

  /* ===========================================================================
   * 7. sticky stack — カードが積み上がる
   *    <div class="mo-stack" data-mo-stack> <article>…</article> × N </div>
   * ======================================================================== */

  var stackCards = [];
  var stackBound = false;

  function initStack(scope) {
    qsaSelf('[data-mo-stack]', scope).forEach(function (stack) {
      if (stack.hasAttribute('data-mo-stack-ready')) return;
      stack.setAttribute('data-mo-stack-ready', '');
      stack.classList.add('mo-stack');

      var cards = Array.prototype.slice.call(stack.children);
      var shrink = num(stack.getAttribute('data-mo-stack-shrink'), 0.06); // 最大縮小率
      stack.style.setProperty('--mo-stack-n', String(cards.length));

      cards.forEach(function (card, i) {
        card.style.setProperty('--mo-i', String(i));
        card.style.zIndex = String(i + 1);
        if (prefersReduced()) return;
        stackCards.push({ el: card, index: i, total: cards.length, shrink: shrink, scale: 1 });
      });
    });

    if (stackBound || !stackCards.length || prefersReduced()) return;
    stackBound = true;

    Ticker.addReader(function () {
      for (var i = 0; i < stackCards.length; i++) {
        var c = stackCards[i];
        if (!c.el.isConnected) continue;
        var r = c.el.getBoundingClientRect();
        // ⚠️ 縮小の起点は「そのカードが sticky で貼り付く位置」でなければならない。
        //    CSS は top: calc(--mo-stack-top + --mo-i * --mo-stack-step) で貼り付ける。
        //    ここを固定値（vh*0.15 など）にすると、貼り付く前から縮み始めて
        //    「スクロールに合わせてカードがフワフワする」不自然な動きになる。
        var stickyTop = c.stickyTop;
        if (stickyTop === undefined || c.measuredAt !== window.innerHeight) {
          stickyTop = pxOfComputed(c.el, 'top');
          c.stickyTop = stickyTop;
          c.measuredAt = window.innerHeight; // 画面サイズが変われば vh 単位が変わるので測り直す
        }
        var passed = clamp((stickyTop - r.top) / Math.max(r.height, 1), 0, 1);
        c.scale = 1 - passed * c.shrink;
      }
    });

    Ticker.addWriter(function () {
      for (var i = 0; i < stackCards.length; i++) {
        var c = stackCards[i];
        if (!c.el.isConnected) continue;
        c.el.style.setProperty('--mo-stack-scale', c.scale.toFixed(4));
      }
    });
  }

  /* ===========================================================================
   * 8. magnetic button — カーソルに吸い付くボタン
   *    <a class="mo-magnetic" data-mo-magnetic="0.3">…</a>
   *    ※ タッチ端末・reduced-motion では無効（誤爆と酔いの原因）
   * ======================================================================== */

  function initMagnetic(scope) {
    if (!fineQuery.matches || prefersReduced()) return;

    qsaSelf('[data-mo-magnetic]', scope).forEach(function (el) {
      if (el.hasAttribute('data-mo-magnetic-ready')) return;
      el.setAttribute('data-mo-magnetic-ready', '');
      el.classList.add('mo-magnetic');

      var strength = clamp(num(el.getAttribute('data-mo-magnetic'), 0.3), 0, 0.6);
      var maxPx = num(el.getAttribute('data-mo-magnetic-max'), 12);
      var rect = null;
      var raf = 0;
      var mx = 0;
      var my = 0;

      function apply() {
        raf = 0;
        el.style.setProperty('--mo-mx', mx.toFixed(1) + 'px');
        el.style.setProperty('--mo-my', my.toFixed(1) + 'px');
      }

      // ⚠️ rect をホバー開始時にキャッシュしたままスクロールされると、
      //    「基準の中心」がズレたままカーソル座標と引き算されるのでボタンが飛ぶ。
      //    ホバー中にスクロール/リサイズが起きたらキャッシュを捨てる。
      var invalidate = function () {
        rect = null;
      };

      el.addEventListener('pointerenter', function () {
        rect = el.getBoundingClientRect();
        el.classList.add('is-mo-magnet-active');
        window.addEventListener('scroll', invalidate, { passive: true });
        window.addEventListener('resize', invalidate, { passive: true });
      });

      el.addEventListener(
        'pointermove',
        function (e) {
          if (!rect) rect = el.getBoundingClientRect();
          mx = clamp((e.clientX - (rect.left + rect.width / 2)) * strength, -maxPx, maxPx);
          my = clamp((e.clientY - (rect.top + rect.height / 2)) * strength, -maxPx, maxPx);
          if (!raf) raf = requestAnimationFrame(apply);
        },
        { passive: true }
      );

      function reset() {
        rect = null;
        mx = my = 0;
        window.removeEventListener('scroll', invalidate);
        window.removeEventListener('resize', invalidate);
        el.classList.remove('is-mo-magnet-active'); // バネで戻す
        el.style.setProperty('--mo-mx', '0px');
        el.style.setProperty('--mo-my', '0px');
      }
      el.addEventListener('pointerleave', reset);
      el.addEventListener('blur', reset);
    });
  }

  /* ===========================================================================
   * 9. flyToCart — 「カートに入れた」を身体で分からせる演出
   *    Motion.flyToCart(document.querySelector('.product img'),
   *                     document.querySelector('[data-cart-icon]'));
   * ======================================================================== */

  function flyToCart(sourceEl, targetEl, options) {
    options = options || {};
    if (prefersReduced() || !sourceEl || !targetEl) {
      bumpCart(targetEl);
      return Promise.resolve();
    }

    var from = sourceEl.getBoundingClientRect();
    var to = targetEl.getBoundingClientRect();
    if (!from.width || !to.width) return Promise.resolve();

    // Web Animations API 未対応環境（古い Safari 等）では演出を諦めて
    // 「カートが跳ねる」だけに落とす。例外で購入導線を壊さないことが最優先。
    if (typeof document.createElement('div').animate !== 'function') {
      bumpCart(targetEl);
      return Promise.resolve();
    }

    var flyer;
    if (sourceEl.tagName === 'IMG') {
      flyer = document.createElement('img');
      flyer.src = sourceEl.currentSrc || sourceEl.src;
      flyer.alt = '';
    } else {
      flyer = document.createElement('div');
      flyer.style.background = options.color || 'currentcolor';
    }
    flyer.className = 'mo-fly';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.style.left = from.left + 'px';
    flyer.style.top = from.top + 'px';
    flyer.style.width = from.width + 'px';
    flyer.style.height = from.height + 'px';
    document.body.appendChild(flyer);

    var dx = to.left + to.width / 2 - (from.left + from.width / 2);
    var dy = to.top + to.height / 2 - (from.top + from.height / 2);
    var endScale = Math.max(0.08, to.width / from.width);
    var dur = num(options.duration, 620);

    var anim = flyer.animate(
      [
        { transform: 'translate3d(0,0,0) scale(1)', opacity: 1 },
        {
          // 中間点を上に持ち上げて放物線にする（直線はロボットっぽく見える）
          transform:
            'translate3d(' +
            dx * 0.5 +
            'px,' +
            (dy * 0.4 - 60) +
            'px,0) scale(' +
            (1 + endScale) / 2 +
            ')',
          opacity: 0.9,
          offset: 0.5,
        },
        {
          transform: 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(' + endScale + ')',
          opacity: 0.2,
        },
      ],
      { duration: dur, easing: 'cubic-bezier(0.5, 0, 0.2, 1)', fill: 'forwards' }
    );

    return anim.finished
      .catch(function () {})
      .then(function () {
        flyer.remove();
        bumpCart(targetEl);
      });
  }

  function bumpCart(targetEl) {
    if (!targetEl || prefersReduced()) return;
    targetEl.classList.remove('mo-cart-bump');
    void targetEl.offsetWidth; // reflow を強制してアニメを再生し直す
    targetEl.classList.add('mo-cart-bump');
    window.setTimeout(function () {
      targetEl.classList.remove('mo-cart-bump');
    }, 500);
  }

  /* ===========================================================================
   * 10. page transition（オプトイン）
   *     <body data-mo-page-transition> を付けたときだけ有効。
   *     View Transitions API に対応していれば何もしない（CSS側が担当）。
   * ======================================================================== */

  function initPageTransition() {
    if (!document.body.hasAttribute('data-mo-page-transition')) return;
    if (document.startViewTransition) return; // ネイティブに任せる
    if (prefersReduced()) return;

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var link = e.target.closest && e.target.closest('a[href]');
      if (!link) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download') || link.hasAttribute('data-no-transition')) return;

      var url;
      try {
        url = new URL(link.href, location.href);
      } catch (err) {
        return;
      }
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.hash) return; // ページ内リンク
      if (!/^https?:$/.test(url.protocol)) return;

      e.preventDefault();
      root.classList.add('is-mo-leaving');
      window.setTimeout(function () {
        location.href = url.href;
      }, 180);
    });

    // bfcache（戻るボタン）で戻ってきたとき、暗転したままにならないよう解除
    window.addEventListener('pageshow', function () {
      root.classList.remove('is-mo-leaving');
    });
  }

  /* ===========================================================================
   * 11. ユーティリティ
   * ======================================================================== */

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  /* ===========================================================================
   * 12. 初期化
   * ======================================================================== */

  function init(scope) {
    scope = scope || document;
    initReveal(scope);
    initSplit(scope);
    initParallax(scope);
    initScrub(scope);
    initCounter(scope);
    initMarquee(scope);
    initStack(scope);
    initMagnetic(scope);
    Ticker.kick();
  }

  function boot() {
    root.classList.add('mo-js'); // インラインスニペットが無い環境の保険
    if (/[?&]motion-debug=1/.test(location.search)) {
      root.setAttribute('data-motion-debug', '');
    }
    init(document);
    initPageTransition();

    // OS 設定が実行中に変わったら追随（macOS/iOS はページ再読込なしで切り替わる）
    var onChange = function () {
      if (prefersReduced()) {
        qsa('[data-mo]').forEach(function (el) {
          el.classList.add('is-mo-in', 'is-mo-done');
        });
      }
    };
    if (reduceQuery.addEventListener) reduceQuery.addEventListener('change', onChange);
    else if (reduceQuery.addListener) reduceQuery.addListener(onChange);

    // Shopify テーマエディタ: セクションの追加・再描画に追随する
    document.addEventListener('shopify:section:load', function (e) {
      init(e.target);
    });
    document.addEventListener('shopify:section:reorder', function () {
      init(document);
    });

    // Ajax カート等で差し込まれた DOM も自動で拾う。
    //
    // ⚠️ ここは素朴に書くと本番で確実に燃える箇所:
    //    「変化があったら init(document)」にすると、カウントダウンタイマーやチャット
    //    ウィジェットのような常時 DOM を触る同居スクリプトがあるだけで、
    //    毎フレーム 10 本の全文書 querySelectorAll が走る（＝スクロールが常時カクつく）。
    //    さらに init 自身が DOM を書き換える（split の span・marquee の複製）ため、
    //    自分の書き込みで自分が再帰的に起動する。
    // 対策: ①「要素が新規追加されたとき」だけ反応する ②追加されたサブツリーだけを
    //       初期化する ③連続した変化はまとめる。
    if (window.MutationObserver) {
      var queued = [];
      var moTimer = 0;

      var flush = function () {
        moTimer = 0;
        var batch = queued;
        queued = [];
        for (var i = 0; i < batch.length; i++) {
          if (batch[i].isConnected) init(batch[i]);
        }
      };

      var mo = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            // テキストノードの差し替え（タイマー表示等）では何も再初期化しない
            if (added[j].nodeType !== 1) continue;
            if (added[j].hasAttribute('data-mo-marquee-clone')) continue; // 自分の書き込み
            queued.push(added[j]);
          }
        }
        if (queued.length && !moTimer) moTimer = window.setTimeout(flush, 100);
      });

      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* ===========================================================================
   * 13. 公開 API
   * ======================================================================== */
  window.Motion = {
    version: VERSION,
    init: init,
    refresh: function () {
      init(document);
      scrubItems.forEach(layoutHscroll);
      Ticker.kick();
    },
    reveal: showReveal,
    /* 未発火の要素を手動で救済する。
     * スクロール位置を JS で書き換えた直後や、Ajax でDOMを差し替えた直後に呼ぶ。
     * （バックグラウンドタブでは scroll イベント自体が来ないため、その保険にもなる） */
    sweep: sweepPassed,
    flyToCart: flyToCart,
    bumpCart: bumpCart,
    splitText: splitText,
    prefersReduced: prefersReduced,
  };
})();
